import { DevelopmentCard, PlayerPoints } from '../catan-view';
import { PlayerProduction } from '../resources';
import { BankTradeRate } from '../trade/bank-trade';
import { TradeAcceptance, TradeCounter, TradeOffer } from '../trade/trade';

export enum StateType {
  Lobby = 'Lobby',
  Setup = 'Setup',
  Turn = 'Turn',
  Discard = 'Discard',
  Robber = 'Robber',
  Win = 'Win',
}

export interface LobbyState {
  state: StateType.Lobby;
}

export interface SetupState {
  state: StateType.Setup;
  turnOrder: string[];
  stepIndex: number;
  currentPlayerId: string | null;
  piece: 'Settlement' | 'Road' | null;
  remainingPlacements: number;
  /**
   * The vertices the placement that is due may use, as reported by the server.
   * Empty when no settlement is due, or when it is not this player's turn.
   * Optional because an API that predates the field sends none.
   */
  legalVertexIds?: number[];
  /** The edges the placement that is due may use, as reported by the server. */
  legalEdgeIds?: number[];
}

/**
 * The play phase: whose turn it is in the announced order, the dice that turn
 * has rolled (empty until it rolls), what the roll paid every player, and the
 * trade the turn is negotiating.
 */
export interface TurnState {
  state: StateType.Turn;
  turnOrder: string[];
  turnIndex: number;
  currentPlayerId: string | null;
  dice: number[];
  production: PlayerProduction[];
  /**
   * The offer on the table, or null when none stands. Public to everyone: the
   * table reads the terms before accepting them. Optional because an API that
   * predates the field sends none.
   */
  offer?: TradeOffer | null;
  /** Every acceptance of the offer, public to everyone, in the order made. */
  acceptances?: TradeAcceptance[];
  /**
   * Every counter-offer to the active player, public to everyone, in the order
   * made. A player is in the acceptances or here, never both.
   */
  counters?: TradeCounter[];
  /**
   * The build targets the server reports for the player whose turn it is: the
   * edges a road may go on, the vertices a settlement may go on and that
   * player's own settlements a city may upgrade. Empty for everyone else, and
   * empty for a piece the player has run out of. Optional because an API that
   * predates the field sends none.
   */
  legalRoadEdgeIds?: number[];
  legalSettlementVertexIds?: number[];
  legalCityVertexIds?: number[];
  /**
   * Whether the server offers the buy to this seat: a card is left, they can
   * afford one and they have not already bought one this turn. Reported by the
   * server so the board offers the buy exactly where it would be accepted.
   * Optional because an API that predates the field sends none.
   */
  canBuyDevelopmentCard?: boolean;
  /**
   * Whether a development card has been played this turn: one a turn. Public,
   * because the table saw it played. Optional because an API that predates the
   * field sends none.
   */
  developmentCardPlayed?: boolean;
  /**
   * Whether the server offers this seat the play of a road building card: it is
   * their turn, they hold one bought on an earlier turn and have played no card
   * this turn. Optional because an API that predates the field sends none.
   */
  canPlayRoadBuilding?: boolean;
  /**
   * Whether the server offers this seat the play of a monopoly card, on the
   * same terms as road building. Optional because an API that predates the
   * field sends none.
   */
  canPlayMonopoly?: boolean;
  /**
   * Whether the server offers this seat the play of a year of plenty card: it
   * is their turn, they hold one bought on an earlier turn and have played no
   * card this turn. Optional because an API that predates the field sends none.
   */
  canPlayYearOfPlenty?: boolean;
  /**
   * How many free roads the active player still has to place from a played road
   * building card; zero when none is being played. Public: while it is above
   * zero the turn is held on those roads. Optional because an API that predates
   * the field sends none.
   */
  freeRoadsRemaining?: number;
  /**
   * What the active player trades each resource at with the bank. Announced by
   * the events that can change it — the turn start, a build and a resumed turn
   * — so the panel cannot show a rate the server would not accept. Optional
   * because an API that predates the field sends none, and empty for a seat
   * whose turn it is not, like the build targets.
   */
  bankTradeRates?: BankTradeRate[];
}

/**
 * The seven's discard obligation: the turn it interrupted, the dice it rolled,
 * who still owes a discard, and how many cards this player has to give up.
 */
export interface DiscardState {
  state: StateType.Discard;
  turnOrder: string[];
  turnIndex: number;
  currentPlayerId: string | null;
  dice: number[];
  pendingPlayerIds: string[];
  cardsToDiscard: number;
}

/** A cell the robber may be moved to, and who a robbery there could take from. */
export interface RobberCell {
  cellId: number;
  victimIds: string[];
}

/**
 * The robber, before it moves — entered from a rolled seven or a played knight. It carries the turn
 * it interrupted and, for the player moving it, the cells it may land on.
 */
export interface RobberState {
  state: StateType.Robber;
  turnOrder: string[];
  turnIndex: number;
  currentPlayerId: string | null;
  dice: number[];
  robberCellId: number | null;
  cells: RobberCell[];
}

/**
 * The end of the game: a player reached ten points on their own turn. It carries
 * the winner, why the game ended, every player's final score and the cards
 * behind the win — the win is where the winner's hidden victory point cards are
 * revealed, so the scores and the cards it names are what every seat reads once
 * the game is over.
 *
 * This is the API's own `WinStateView`, so the revealed cards are named
 * `developmentCards` here as they are in the view the server sends — the win's
 * event calls the same set `cards`, and the reducer maps it across. A reload of
 * a finished game is read off the view, so the name must be the view's. The two
 * collections are the view's own types, because they are the view's own shapes:
 * a score and a development card the table may read.
 */
export interface WinState {
  state: StateType.Win;
  winnerId: string;
  reason: string;
  scores: PlayerPoints[];
  developmentCards: DevelopmentCard[];
}

export type CatanState =
  | LobbyState
  | SetupState
  | TurnState
  | DiscardState
  | RobberState
  | WinState;